import { Badge } from "@mantine/core";
import Link from "next/link";
import dayjs from "dayjs";
import { BsFillPeopleFill } from "react-icons/bs";
import { FaBed, FaBath, FaDog } from "react-icons/fa";
import { IoIosPin } from "react-icons/io";
import GallerySlider from "../gallery-slider/GallerySlider";
import StarRating from "../star-rating/StarRating";
import CustomBadge from "../badges/CustomBadge";
import ListingStars from "../shared/ListingStars";

const ImageSlider = ({ id, images, pool }) => {
  return (
    <div className="w-full flex-shrink-0 p-3 sm:w-72">
      <GallerySlider
        ratioClass="aspect-w-1 aspect-h-1"
        galleryImgs={images}
        className="h-full w-full overflow-hidden rounded-2xl"
        uniqueID={`PropertyCardH-${id}`}
      />

      {pool && (
        <CustomBadge
          className="absolute left-5 top-5 !bg-orange-500"
          desc="Zwembad"
        />
      )}
    </div>
  );
};

const ListingInfo = ({ data }) => {
  return (
    <>
      <div className="inline-grid grid-cols-2 gap-5 md:gap-2 xl:grid-cols-4">
        <div className="flex items-center space-x-2">
          <span className="inline-block">
            <BsFillPeopleFill className="text-lg" />
          </span>
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {data?.maxNumberOfPersons}{" "}
            <span>
              {data?.maxNumberOfPersons === 1 ? "persoon" : "personen"}
            </span>
          </span>
        </div>

        <div className="flex items-center space-x-2">
          <span className="inline-block">
            <FaBed className="text-lg" />
          </span>
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {data?.numberOfBedrooms === 0 ? 1 : data?.numberOfBedrooms}
            <span>
              {" "}
              {data?.numberOfBedrooms > 1 ? "slaapkamers" : "slaapkamer"}
            </span>
          </span>
        </div>

        {data?.numberOfBathrooms > 0 && (
          <div className="flex items-center space-x-2">
            <span className="inline-block">
              <FaBath className="text-lg" />
            </span>
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              {data?.numberOfBathrooms}{" "}
              <span>
                {data?.numberOfBathrooms === 1 ? "badkamer" : "badkamers"}
              </span>
            </span>
          </div>
        )}

        {data?.numberOfPets > 0 && (
          <div className="flex items-center space-x-2">
            <span className="inline-block">
              <FaDog className="text-lg" />
            </span>
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              {data?.numberOfPets}{" "}
              <span>
                {data?.numberOfPets === 1 ? "huisdier" : "huisdieren"}
              </span>
            </span>
          </div>
        )}
      </div>

      {data?.country && (
        <>
          <div className="flex items-center space-x-2">
            <span className="inline-block">
              <IoIosPin className="text-lg text-green-500" />
            </span>
            <span className="text-sm font-light text-inherit antialiased">
              {data?.country}
              {data?.city && `, ${data?.city}`}
              {data?.region && ` (${data?.region})`}
            </span>
          </div>
        </>
      )}
    </>
  );
};

const Content = ({ data }) => {
  const score = (data?.enqePoints / data?.enqeCount).toFixed(1);
  return (
    <div className="flex flex-grow flex-col items-start p-3 sm:pr-6">
      <div className="w-full space-y-4">
        <div className="flex flex-wrap">
          {data?.numberOfPets > 0 && (
            <Badge color="green" mr={5} mb={5} mt={5}>
              Huisdieren toegestaan
            </Badge>
          )}
          {data?.skiArea && (
            <Badge color="red" mr={5} mb={5} mt={5}>
              In een skigebied
            </Badge>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <h2 className="text-lg font-medium">
            <span className="line-clamp-2">{data?.shortDescription}</span>
          </h2>
        </div>

        <ListingInfo data={data} />
        <div className="w-14 border-b border-neutral-100 dark:border-neutral-800" />

        <div className="w-full items-center sm:flex">
          <div className="basis-full">
            <StarRating
              reviewCount={data?.reviews}
              point={!isNaN(score) ? score : null}
            />
          </div>
          <div className="basis-full">
            <div className="justify-self-end">
              {data?.availability && (
                <div className="">
                  <div
                    className="w-full text-center capitalize text-neutral-500 dark:text-neutral-400"
                    style={{ fontSize: 14 }}
                  >
                    {dayjs(data?.availability?.arrivalDate).format("MMM DD")} -{" "}
                    {dayjs(data?.availability?.departureDate).format("MMM DD")}
                  </div>
                  <div className="w-full text-center" style={{ fontSize: 13 }}>
                    <b style={{ fontSize: 15 }}>
                      {Intl.NumberFormat("nl-NL", {
                        style: "currency",
                        currency: "EUR",
                      }).format(data?.availability?.pricePerNight)}
                    </b>{" "}
                    <span style={{ fontSize: 13 }}>/nacht</span>
                  </div>
                </div>
              )}
              <span className="flex items-center justify-center rounded border border-secondary-500 px-3 py-2 text-base font-medium leading-none text-secondary-500">
                Bekijk meer
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ListingCard = ({ className = "", data }) => {
  return (
    <div
      className={`nc-PropertyCardH group relative overflow-hidden rounded-3xl border border-neutral-100 bg-white transition-shadow hover:shadow-xl dark:border-neutral-800 dark:bg-neutral-900 ${className}`}
      data-nc-id="PropertyCardH"
    >
      <div className="flex h-full w-full flex-col sm:flex-row sm:items-center">
        <ImageSlider id={data.id} images={data.images} pool={data?.pool} />
        <Link
          className="flex h-full w-full flex-col sm:flex-row sm:items-center"
          href={{
            pathname: `/vakantiehuizen/${data?.slug}`,
            query: {
              ...(data?.availability?.arrivalDate && {
                arrival: dayjs(data?.availability?.arrivalDate).format(
                  "YYYY-MM-DD"
                ),
              }),
              ...(data?.availability?.departureDate && {
                departure: dayjs(data?.availability?.departureDate).format(
                  "YYYY-MM-DD"
                ),
              }),
            },
          }}
        >
          <Content data={data} />
        </Link>
      </div>
      <div className="absolute right-5 top-5 text-neutral-6000 dark:text-neutral-400 sm:right-3 sm:top-3">
        <ListingStars totalStars={data?.numberOfStars} />
      </div>
    </div>
  );
};

export default ListingCard;
